import { CardTitleLookup } from '@/constants/ReportDashboard/cards-metadata'
import { useFactorLensContext, useSelectedFactorBox, useViewedFactor, useViewedFactorType } from '@/contexts/FactorLensContext/hooks'
import { useIsAnalysisLoading } from '@/contexts/RADashboardDataContext/hooks'
import { useTheme } from '@/contexts/RADashboardThemeContext/hooks'
import { FactorLensDataType, FactorLensState } from '@/interfaces/factorLens.interface'
import { CardProps } from '@/interfaces/raDashboard.interface'
import { cardStyle, cardSubtitleStyle, cardTitleStyle } from '@/styles/reportDashboardStyles'
import { CircularProgress } from '@mui/material'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import React from 'react'
import FactorContributions from './FactorContributions'
import FactorDataTypeSelector from './FactorDataTypeSelector'
import FactorLensGrid from './FactorLensGrid'
import { useFactorRiskColors } from './factorRiskColors'

/**
 * Standalone Factor Decomposition (pyramid) card, split out of FactorLensRisk and
 * wired to the shared FactorLensContext: the data type is shared with the other
 * Factor Lens cards, and clicking a factor box here drives the Waterfall/Breakdown
 * via setFactorBox. The Top/Bottom contributions panel is shown on the right for
 * Portfolio (its collapse state stays local).
 */
const FactorGridCard: React.FC<CardProps> = (props) => {
  const theme = useTheme()
  const factorRiskColors = useFactorRiskColors()

  const { setFactorBox } = useFactorLensContext()
  const selectedDataType = useViewedFactorType()
  const selectedFactorBox = useSelectedFactorBox()
  const factorData = useViewedFactor()

  const dataLoading = useIsAnalysisLoading()

  // Collapse state of the contributions panel is purely visual — keep it local.
  const [contributionsShown, setContributionsShown] = React.useState(true)

  // FactorLensGrid consumes a FactorLensState; build it from the shared context.
  const cardState: FactorLensState = {
    selectedDataType,
    selectedFactorBox,
    contributionsShown,
    waterfallShown: true,
  }

  const cardLoading = () => (
    <div style={{ margin: 5 }}>
      <CircularProgress size={'3rem'} aria-label="Loading…" />
    </div>
  )

  const cardContent = () => (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <FactorDataTypeSelector />

      {/* Pyramid grid + Top/Bottom contributions on the right (Portfolio only),
          mirroring the main card. FactorContributions provides its own collapsible box. */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden', minWidth: 0 }}>
        <div style={{ flex: 6, minWidth: 0, height: '100%' }}>
          <FactorLensGrid
            gridData={factorData}
            cardState={cardState}
            setSelected={setFactorBox}
            colors={factorRiskColors}
          />
        </div>
        {selectedDataType === FactorLensDataType.Portfolio && (
          <FactorContributions
            shown={contributionsShown}
            toggleVisibility={() => setContributionsShown((prev) => !prev)}
            cardPrefs={props.cardPrefs}
            onCardPrefsChange={props.onCardPrefsChange}
          />
        )}
      </div>
    </div>
  )

  return (
    <div
      style={{
        ...cardStyle,
        width: '100%',
        height: '100%',
        backgroundColor: theme.cardBackground,
        border: `1px solid ${theme.border}`,
      }}
    >
      <Stack padding={4} width={'100%'} sx={{ boxSizing: 'border-box', height: '100%' }}>
        <Typography style={{ ...cardTitleStyle, color: theme.textPrimary }}>
          {CardTitleLookup[props.cardId]}
        </Typography>
        <Typography style={{ ...cardSubtitleStyle, color: theme.textSecondary }}>
          Risk decomposition by market factor groups
        </Typography>

        {dataLoading ? cardLoading() : cardContent()}
      </Stack>
    </div>
  )
}

export default FactorGridCard
